// src/pages/Menu/Finance/OutcomeFormModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { X, Save, PlusCircle, Trash2 } from 'lucide-react';
import Swal from 'sweetalert2';

// 👉 Helper local (mismo formato que en la lista)
const Money = (v) => {
  const num = Number(v) || 0;
  // Mostrar sin redondeo, con todos los decimales que tenga
  const parts = num.toString().split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decimalPart = parts[1] || '';
  return `$${integerPart}${decimalPart ? ',' + decimalPart : ''}`;
};

export default function OutcomeFormModal({ open, onClose, rut, concepts = [], initialData, onSave }) {
  const isEdit = !!initialData?.id;

  // Usar el RUT del registro existente si estamos editando, sino el prop rut
  const currentRut = useMemo(() => {
    if (isEdit && initialData?.rut) {
      return initialData.rut;
    }
    return rut;
  }, [isEdit, initialData?.rut, rut]);

  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [companyName, setCompanyName] = useState('');

  const [lines, setLines] = useState([{ conceptId: '', amount: '', activity: '' }]);

  useEffect(() => {
    if (!open) return;
    if (initialData?.id) {
      setDate(initialData.date || new Date().toISOString().slice(0, 10));
      setDescription(initialData.description || '');
      setCompanyName(initialData.companyName || '');
      setLines(
        (initialData.lines || []).map((l) => ({
          conceptId: l?.conceptId || l?.concept?.id || '',
          amount: Number(l?.amount || 0),
          activity: l?.activity || '', // Preservar el campo activity si existe
        })),
      );
    } else {
      setDate(new Date().toISOString().slice(0, 10));
      setDescription('');
      setCompanyName('');
      setLines([{ conceptId: '', amount: '', activity: '' }]);
    }
  }, [open, initialData]);

  const total = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.amount) || 0), 0),
    [lines],
  );

  const addLine = () => setLines((prev) => [...prev, { conceptId: '', amount: '', activity: '' }]);
  const delLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));
  const updLine = (idx, patch) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const handleSubmit = async () => {
    try {
      const payload = {
        rut: currentRut,
        date,
        description: description || null,
        companyName: companyName || null,
        lines: lines
          .filter((l) => l.conceptId && Number(l.amount) > 0)
          .map((l) => ({
            conceptId: Number(l.conceptId),
            amount: Number(l.amount),
            activity: l.activity || null, // Incluir el campo activity si existe
          })),
      };

      if (!payload.lines.length) {
        return Swal.fire('Valida', 'Agrega al menos una línea con monto > 0', 'warning');
      }
      await onSave?.(payload);
    } catch (e) {
      Swal.fire('Error', e.message || 'No se pudo guardar', 'error');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40">
      <div className="bg-white w-full max-w-6xl rounded-xl shadow-lg overflow-hidden max-h-[95vh] flex flex-col">
        {/* Header con Banner */}
        <div className="relative">
          <div className="bg-gradient-to-r from-[#09274A] to-[#0488AF] rounded-t-xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-white">
                  {isEdit ? `Editar registro #${initialData?.id}` : 'Nuevo egreso'}
                </h2>
                <p className="text-sm text-white/90 mt-1">
                  {isEdit ? 'Modifica los datos del registro de egreso' : 'Registra un nuevo egreso en el libro diario'}
                </p>
              </div>
              <button 
                onClick={onClose} 
                className="p-2 rounded-lg hover:bg-white/10 text-white transition"
                aria-label="Cerrar"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-600">Fecha (contable)</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full mt-1 rounded border px-3 py-2"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Empresa / Pagado a</label>
              <input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full mt-1 rounded border px-3 py-2"
                placeholder="Nombre visible de la empresa"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-600">Detalle</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full mt-1 rounded border px-3 py-2"
              placeholder="Ej: Servicios profesionales, Materiales, etc."
            />
          </div>

          {/* Desglose contable - Estilo Excel */}
          <div className="rounded border-2 border-gray-300 overflow-hidden bg-white">
            <div className="px-4 py-2.5 border-b-2 border-gray-300 bg-gray-100 font-semibold text-gray-800">
              Libro Diario — Desglose de Conceptos
            </div>
            <div className="p-2 overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b-2 border-gray-300">
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 border-r">Concepto</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 border-r">Detalle</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700 border-r">Monto</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-700">
                      <button
                        onClick={addLine}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border hover:bg-blue-50 text-blue-600"
                        title="Agregar línea"
                      >
                        <PlusCircle size={14} /> Agregar
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => {
                    const c = concepts.find((x) => Number(x.id) === Number(l.conceptId));
                    return (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 border-r">
                          <select
                            value={l.conceptId}
                            onChange={(e) => updLine(i, { conceptId: e.target.value })}
                            className="w-full rounded border px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">(Seleccione concepto)</option>
                            {concepts.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 border-r">
                          <input
                            type="text"
                            value={l.activity || ''}
                            onChange={(e) => updLine(i, { activity: e.target.value })}
                            className="w-full rounded border px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Ej: Enero 2025"
                          />
                        </td>
                        <td className="px-3 py-2 border-r">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={l.amount}
                            onChange={(e) => updLine(i, { amount: e.target.value })}
                            className="w-full rounded border px-2 py-1.5 text-right font-semibold focus:ring-2 focus:ring-red-500 focus:border-red-500"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => delLine(i)}
                            className="px-2 py-1 rounded border text-red-600 hover:bg-red-50 transition"
                            title="Eliminar línea"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-gray-400 bg-gray-100 font-bold">
                    <td className="px-3 py-2.5 border-r">TOTAL</td>
                    <td className="px-3 py-2.5 border-r"></td>
                    <td className="px-3 py-2.5 text-right text-red-700 border-r">{Money(total)}</td>
                    <td className="px-3 py-2.5"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded border hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Save size={16} /> Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

